/**
 * Where a result row came from: which file, which row, and that row's values
 * as read through the column mapping. A panel from the right, so the result
 * stays in view.
 */

import { Fragment } from 'react';
import { Note, SideDrawer } from '../../shared/ui';
import { useCanonicalFieldsQuery } from '../../queries/firm';
import { ApiError } from '../../api/errors';
import { useMutation } from '@tanstack/react-query';
import { useLineageQuery } from '../../queries/analysis';
import { downloadFile } from '../files/FileTable';
import { ErrorState } from './states';
import { Spinner } from '../../shared/ui';
import { Button } from '@mui/material';

export function SourceLineageDrawer({
	runId,
	row,
	onClose,
	/**
	 * What `AnalysisDetail` already knows and this endpoint does not answer:
	 * which analysis and run this row belongs to, and a plain rendering of the
	 * row's own cells — the closest real substitute for the approved mock's
	 * authored "claim" sentence (`data.statement`), built from the same values
	 * already on screen rather than invented prose.
	 */
	context,
}: {
	runId: string | null;
	row: number | null;
	onClose: () => void;
	context: {
		analysisLabel: string;
		runLabel: string;
		rowSummary: string;
	} | null;
}) {
	const open = runId !== null && row !== null;
	const { data, error, isPending, refetch } = useLineageQuery(
		open ? `/analysis-runs/${runId}/lineage?row=${row}` : null,
	);
	const download = useMutation({ mutationFn: downloadFile });
	const fields = useCanonicalFieldsQuery();
	const labelOf = (key: string) =>
		fields.data?.fields.find((field) => field.field === key)?.label ?? key;

	const noLineage = error instanceof ApiError && error.status === 409;

	return (
		<SideDrawer
			open={open}
			onClose={onClose}
			title="Where this row came from"
			subtitle={
				data
					? `${data.fileName} · row ${data.sourceRowNumber.toLocaleString('en-IN')}`
					: undefined
			}
			footer={
				<div className="spread">
					<Button variant="outlined" onClick={onClose}>
						Close
					</Button>
					{data && (
						<div className="row" style={{ gap: 'var(--s2)' }}>
							<Button
								loading={download.isPending}
								variant="outlined"
								size="small"

								disabled={download.isPending}
								onClick={() => download.mutate(data.fkFileId)}
							>
								Download original file
							</Button>
						</div>
					)}
				</div>
			}
		>
			{isPending && <Spinner label="Loading…" />}

			{noLineage ? (
				<Note tone="gray" title={error.message}>
					{error.detail}
				</Note>
			) : (
				<ErrorState error={error} onRetry={refetch} />
			)}

			{data && (
				<div className="stack">
					{context && (
						<div className="lin-claim">
							<div className="lin-claim-t">
								{context.rowSummary}
							</div>
							<div className="m">
								{context.analysisLabel} · {context.runLabel}
							</div>
						</div>
					)}

					<dl className="kv">
						<dt>Source file</dt>
						<dd>{data.fileName}</dd>
						<dt>Row in source file</dt>
						<dd>{data.sourceRowNumber.toLocaleString('en-IN')}</dd>
					</dl>

					<div>
						<h4>Values on this row</h4>
						<dl className="kv">
							{Object.entries(data.values).map(
								([field, value]) => (
									<Fragment key={field}>
										<dt>{labelOf(field)}</dt>
										<dd>
											{value === null || value === '' ? (
												<span className="mut">—</span>
											) : (
												value
											)}
										</dd>
									</Fragment>
								),
							)}
						</dl>
					</div>
				</div>
			)}
		</SideDrawer>
	);
}
